









































































































































































































import Vue from 'vue'
import { debounce } from 'debounce'
import { mapGetters } from 'vuex'
import BillingDiscountForm from '@/components/BillingDiscountForm.vue'
import subscriptionMixin from '@/mixins/subscription-mixin.js'
import SubscriptionOption, { StripeInterval } from '@/types/SubscriptionOption.interface'
import { Plan } from '@/types/Subscription.interface'
interface Addon {
    price: number
    count: number
}
export default Vue.extend({
    mixins: [subscriptionMixin],
    components: { BillingDiscountForm },
    data() {
        return {
            legacyAddons: {
                autoresponder: null,
                form: null,
                templates: null
            },
            discountCode: null,
            freeAddons: {
                Starter: ['1 User Included'],
                Operator: ['3 Users Included'],
                Team: ['10 Users Included']
            },
            currNum: 0,
            today: null,
            upcoming: false,
            freeUsers: null,
            newPlan: null,
            newUsers: null,
            oldPlan: null,
            oldUsers: null,
            reactivating: false
        }
    },
    mounted() {
        //  Initialize our component
        this.init()
    },
    computed: {
        ...mapGetters(['getLimit', 'subscriptionOptions', 'trialing', 'user', 'isApp']),
        billedUsers(): number {
            //  Return the number of selected users subtracted from the free users for their plan
            return this.newUsers - this.freeUsers
        },
        //  Return true if user has canceled
        canceled(): boolean {
            return (
                this.subscription &&
                (this.subscription.status === 'canceled' ||
                    this.subscription.cancel_at_period_end)
            )
        },
        discount(): string {
            if (this.subscription && this.subscription.discount) {
                return this.subscription.discount.coupon.percent_off
            }

            return null
        },
        isActive(): boolean {
            if (!this.subscription || !this.oldPlan) return false

            //  Return true if subscription is active and the tenant is not trialing/canceled
            return (
                this.subscription.status == 'active' &&
                !this.oldPlan.metadata.default_start_plan
            )
        },
        oldPlanTotal(): string {
            if (!this.oldPlan || !this.isActive) return '0'

            //  Get the base price of the old plan
            const basePrice = this.oldPlan.amount / 100

            //  Calculate the cost per user for the old plan = billedUsers * costPerUser * 12 (if yearly)
            const usersCost =
                (this.oldUsers - this.freeUsers) *
                this.costPerUser(this.oldPlan.metadata.product_tier) *
                (this.oldPlan.interval == 'year' ? 12 : 1)

            //  Get the values of all of our addons
            const addons = Object.values(this.legacyAddons)

            //  We need to grab all of our addons prices but make it a valid price by moving decimal over 2 spaces
            const prices = addons.map((x: Addon) => (x ? x.price * 0.01 : 0))

            //  Add up all of our prices to get the total
            const addonsPrice = prices.reduce((acc: number, curr: number) => acc + curr)

            //  Return the plan price + user cost + addon cost
            const noDiscount = basePrice + usersCost + addonsPrice

            //  Our discountFactor will be our final price * (this.discount / 100) since it applies discount as percent
            //  If we don't have a discount, we default to 0 so we don't end up applying anything
            const discountFactor = this.discount ? noDiscount * (this.discount / 100) : 0

            //  Apply our discount factor to get our final price
            const finalPrice = noDiscount - discountFactor

            //  Format our price
            return parseFloat(finalPrice.toString()).toFixed(2)
        },
        operator(): boolean {
            //  Grab our current role
            const role = this.$store.getters.userRole(this.$store.getters.user)

            //  Check if this role is an operator
            return window.getProps(role, ['name']) === 'Operator'
        },
        plan(): SubscriptionOption {
            //  If a new plan was selected, use new plan to display information
            if (this.newPlan) return this.newPlan

            //  If no current subscription, return nothing
            if (!this.subscription) return null

            //  If the user is trialing or canceled, return nothing
            if (this.subscription.status === 'canceled') return null

            //  Return the stripe plan from the subscription
            return this.$store.getters.plan
        },
        planChanged(): boolean {
            //  The user added/removed something if there's a newPlan, or the user count changed, or the addons changed
            return this.newPlan || this.oldUsers != this.newUsers || this.addonsChanged
        },
        planInterval(): StripeInterval {
            //  If no plan, return nothing
            if (!this.plan) return null

            //  Return the plan's interval
            return this.plan.interval
        },
        planPrice(): number {
            //  If no plan, return nothing
            if (!this.plan) return null

            //  Return the plan's dollar amount
            return this.plan.amount / 100
        },
        planProductTier(): string {
            //  If no plan, return nothing
            if (!this.plan) return null

            //  If product tier not specified, return Custom
            if (!this.plan.metadata || !this.plan.metadata.product_tier) return 'Custom'

            //  Return plan's product tier
            return this.plan.metadata.product_tier
        },
        planTotal(): string {
            //  Calculate the base price for the plan
            const basePrice = this.planPrice

            //  Calculate the cost per user for the plan taking into account monthly vs annual
            const usersPrice =
                this.billedUsers *
                this.costPerUser(this.planProductTier) *
                (this.planInterval == 'year' ? 12 : 1)

            //  Get the values of all of our addons
            const addons = Object.values(this.legacyAddons)

            //  We need to grab all of our addons prices but make it a valid price by moving decimal over 2 spaces
            const prices = addons.map((x: Addon) => (x ? x.price * 0.01 : 0))

            //  Add up all of our prices to get the total
            const addonsPrice = prices.reduce((acc: number, curr: number) => acc + curr)

            //  No discount is our total cost
            const noDiscount = basePrice + usersPrice + addonsPrice

            //  Our discountFactor will be our final price * (this.discount / 100) since it applies discount as percent
            //  If we don't have a discount, we default to 0 so we don't end up applying anything
            const discountFactor = this.discount ? noDiscount * (this.discount / 100) : 0

            //  Apply our discount factor to get our final price
            const finalPrice = noDiscount - discountFactor

            //  Format our price
            return parseFloat(finalPrice.toString()).toFixed(2)
        },
        usersRemoveDisabled(): boolean {
            //  Removing users is disabled if the plan tier is starter, the user count is 1, or the product tier is team and the selected users is 5
            return (
                //  Can't have less than 1 user
                this.newUsers <= 1 ||
                //  Min users on team plan is 5
                (this.planProductTier == 'Team' && this.newUsers <= 5) ||
                //  Don't allow removing user seats that have already been purchased
                this.newUsers == this.oldUsers
            )
        }
    },
    methods: {
        calculateToday: debounce(async function (): Promise<void> {
            const payload = {
                users: this.billedUsers,
                ...this.plan
            }

            //  Attach the selected discount if available
            if (this.discountCode) payload.discount = this.discountCode

            //  Start getting upcoming
            this.upcoming = true

            //  Set our current variable to what number we are on
            const curr = ++this.currNum

            //  Get our upcoming invoice
            const response = await this.$store.dispatch('subscriptionUpcoming', payload)

            //  If curr is NOT the same as currNum, return
            if (curr !== this.currNum) return

            //  Stop getting upcoming
            this.upcoming = false

            //  Stripe throws an error trying to get upcoming invoices for canceled subscriptions
            //  In this case, just return the price of the plan
            if (!response) this.today = parseFloat(this.planTotal).toFixed(2)
            //  Else set our price
            else this.today = (response.amount_due * 0.01).toFixed(2)
        }, 400),
        async continueClicked(): Promise<void> {
            //  Validate our data is right
            const valid = await this.$validator.validateAll()

            //  If invalid, do nothing
            if (!valid) return

            //  Our payload
            const payload = {
                plan: this.plan,
                users: this.billedUsers,
                todaysCharge: this.today
            }

            //  Proceed
            return this.onPlanSelectedConfirm(payload)
        },
        costPerUser(tier: string): number {
            //  Starter plans can't add users
            if (tier == 'Starter') return 10

            //  Operator plans can add users for $10 per user
            if (tier == 'Operator') return 10

            //  Team plans can add users for $15 per user
            if (tier == 'Team') return 15

            //  Default to $10 per user
            return 10
        },
        discountSet(code: string): void {
            //  Set the new discount code
            this.discountCode = code

            //  Update next billing price to account for discount
            this.calculateToday()
        },
        getAddonCounts(): void {
            //  Get the subscription items
            const items =
                window.getProps(this.subscription, ['items', 'data'], null) || []

            //  Iterate through each item to get their count
            items.forEach((item) => {
                //  If this item is not a feature product, skip it
                if (!item.plan.metadata.feature_product) return

                //  Else, check what the feature product is
                const feature = item.plan.metadata.feature_product

                //  If this item is a user then skip it
                if (feature === 'user') return

                //  Our count will be whatever this current quantity is
                const count = item.quantity

                //  Set our count in our legacy addon
                this.legacyAddons[feature] = {
                    count: count,
                    price: item.plan.amount * count
                }
            })
        },
        getPlanByIndex(index: number): void {
            //  First, set the old plan
            this.oldPlan = this.$store.getters.plan

            //  If no index, do nothing
            if (!index) return (this.newPlan = null)

            //  Find the plan
            const plan = this.subscriptionOptions.find(
                (option) =>
                    option.metadata.index == index && option.metadata.is_active === 'true'
            )

            //  If no plan, do nothing
            if (!plan) return (this.newPlan = null)

            //  If the plan is already selected, do nothing
            if (plan.id == this.oldPlan.id && this.isActive) return (this.newPlan = null)

            //  Otherwise set the new plan
            return (this.newPlan = plan)
        },
        getPlanUsers(plan: Plan): number {
            //  Get the number of free users the subscription allows (including addons)
            const userLimit = this.getLimit('user', true, plan)

            //  Save the old user count
            this.oldUsers = userLimit

            //  Get the free users for the plan (not including addons)
            this.freeUsers = this.getLimit('user', false, plan)

            //  Return the user limit
            return userLimit
        },
        init(): void {
            //  Check if the user selected a new plan from the BillingSubscriptionForm component (will redirect here with index in query parameter)
            this.newPlan = this.getPlanByIndex(this.$route.query.index)

            //  Set the new free user count for the selected plan
            this.newUsers = this.getPlanUsers(this.newPlan)

            //  Check if query params are telling us to add a user automatically
            const userQuery = this.$route.query.addon == 'user'

            if (userQuery) this.newUsers += 1

            //  Get the current count of addons the user has
            this.getAddonCounts()
        },
        onPlanChanged(): void {
            //  If the plan was upgraded, reset the new plan
            this.newPlan = null

            if (this.$route.query) {
                //  Remove query params
                let query = Object.assign({}, this.$route.query)

                if (query.index) delete query.index

                if (query.addon) delete query.addon

                this.$router.replace({ query })
            }

            //  Refresh user / addon counts for the newly upgraded plan
            this.init()
        },
        popIntercomMessenger(): void {
            //  @ts-ignore If we are on the mobile app, then call our Cordova plugin
            if (this.isApp) intercom.displayMessenger()
            // Pops the intercom messenger through the id bound to the hidden span element
            else window.popSupportMessenger()
        },
        async reactivateSub(): Promise<void> {
            //  Pop a dialog
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                color: 'primary',
                icon: 'mdi-credit-card-outline',
                title: 'Confirm Subscription Reactivation',
                text: `Are you sure you want to reactivate your subscription? These changes will take effect immediately and you will be charged $${this.today}.`,
                callback: async (result: boolean): Promise<void> => {
                    //  If result was false, do nothing
                    if (!result) return

                    //  Else, trigger a load spinner
                    this.reactivating = true

                    //  Reactivate
                    const response = await this.$store.dispatch('subscriptionReactivate')

                    //  Stop the loader
                    this.reactivating = false

                    //  Notify the user
                    this.$store.commit('appNotificationBarSet', {
                        message: `Subscription Reactivated ${
                            response ? 'Successfully!' : 'Unsuccessfully'
                        }`
                    })
                }
            })
        },
    },
    watch: {
        billedUsers(): void {
            this.calculateToday()
        },
        newPlan(): void {
            this.calculateToday()
        },
        subscription(val, prev): void {
            //  If previous was null but we now have a value, init
            if (!prev && val) this.init()
        }
    }
})
